import { NoddiAsyncError } from 'noddi-types';
import { cn, cva, NoddiIcon, NoddiIconName } from 'noddi-ui-common';
import { commonTexts } from 'noddi-util';
import { useEffect, useRef, useState, type Key, type ReactNode } from 'react';
import { Card } from '../../base/card';
import { shouldSkipRowNavigation } from '../DataDisplay/Table/components/shouldSkipRowNavigation';
import { useRowKeyboardNavigation } from '../DataDisplay/Table/components/useRowKeyboardNavigation';
import { NoddiEmptyContent } from '../Feedback/Empty/NoddiEmptyContent';
import { ApiErrorMessageInternal } from '../Feedback/Errors/ApiErrorMessageInternal';
import { NoddiSkeleton } from '../Feedback/NoddiSkeleton';
import { NoddiButton } from '../Inputs/NoddiButton';
import { NoddiIconButton } from '../Inputs/NoddiIconButton';
import { NoddiInput } from '../Inputs/NoddiInput';
import { NoddiSeparator } from '../Other/NoddiSeparator';
import { NoddiCardHeader } from './NoddiCardHeader';

type Translations = typeof commonTexts.listCard;

type Props<T> = {
  title: string;
  icon?: NoddiIconName;
  items: T[];
  getItemKey: (item: T) => Key;
  /** One item's content. Row layout owns padding and dividers; grid items own their card surface. */
  renderItem: (item: T) => ReactNode;
  /** Cards keep the same loading, error, search and footer behavior as rows. */
  layout?: 'rows' | 'grid';
  /** Four compact cards per row when the container has room. */
  gridColumns?: 3 | 4;
  /** Page scrolling lets long grids grow beyond the default capped list height. */
  scrollMode?: 'contained' | 'page';
  /**
   * Opens the item, e.g. its detail sheet: a click anywhere on the row, or Enter/Space on a focused
   * row. Buttons and links inside the row keep their own action.
   */
  onItemClick?: (item: T) => void;
  /**
   * Tints the item's row as pinned, the warm fill `NavioPinnedNotes` uses. Sorting pinned items
   * first stays with the caller, which owns the order.
   */
  isItemPinned?: (item: T) => boolean;
  /** Leaves the item count out of the header, e.g. when the rows carry their own numbers. */
  hideCount?: boolean;
  /** The header count when the card lists only some items, e.g. the latest three of every ticket. */
  titleCount?: number;
  /** Text the in-card search matches. Omit it and the card never offers search. */
  getSearchText?: (item: T) => string;
  /** Search appears only above this many items: a short list is scanned, not searched. */
  searchThreshold?: number;
  /** Header actions left of the search toggle, e.g. a `NoddiLink` to the parent resource. */
  headerNode?: ReactNode;
  /**
   * A link under the title to everything the card samples, e.g. "View all tickets" to a tab. Under the title, not
   * beside it: the header's right side is kept for actions such as "Create new ticket".
   */
  viewAll?: { label: string; onClick: () => void };
  /** Full-width controls below the title, e.g. server search, filters and sorting. */
  toolbar?: ReactNode;
  /** Pinned below the list (totals, add actions) so it stays visible while the rows scroll. */
  footer?: ReactNode;
  /** Rows still loading: skeleton rows, no count and no search until they arrive. */
  isPending?: boolean;
  /** The rows failed to load; shown in the body so a failure never reads as an empty list. */
  error?: NoddiAsyncError | null;
  /** Offers a retry on the error, e.g. a refetch of the rows. */
  onRetry?: () => void;
  isRetrying?: boolean;
  /** `small` matches `NoddiCard size='small'` neighbors in a side column. */
  size?: 'small' | 'medium';
  translations?: Partial<Translations>;
  /**
   * Takes its container's full height and scrolls the rows inside it, header and search staying put,
   * e.g. as the body of a sheet. Without it the rows scroll in a fixed-height box, sized for a page.
   */
  fillHeight?: boolean;
  className?: string;
};

const DEFAULT_SEARCH_THRESHOLD = 8;
const SKELETON_ROWS = [0, 1, 2];

const listVariants = cva('text-sm', {
  variants: {
    layout: {
      rows: 'divide-y divide-border',
      grid: 'grid grid-cols-1 items-stretch gap-4 p-4 @2xl:grid-cols-2'
    },
    gridColumns: {
      3: '@7xl:grid-cols-3',
      4: '@4xl:grid-cols-3 @5xl:grid-cols-4'
    }
  }
});

// Header, rows and footer share one inset so the row content lines up with the title.
const insetVariants = cva('', {
  variants: {
    size: {
      small: 'px-4 py-3',
      medium: 'px-5 py-4'
    }
  }
});

/**
 * A card whose body is a list of items: header with count, rows that scroll inside a capped height,
 * optional search and footer. Rows are loaded in full; search filters them on the client.
 */
export function NavioListCard<T>({
  title,
  icon,
  items,
  getItemKey,
  renderItem,
  layout = 'rows',
  gridColumns = 3,
  scrollMode = 'contained',
  onItemClick,
  isItemPinned,
  hideCount,
  titleCount,
  getSearchText,
  searchThreshold = DEFAULT_SEARCH_THRESHOLD,
  headerNode,
  viewAll,
  toolbar,
  footer,
  isPending,
  error,
  onRetry,
  isRetrying,
  size = 'medium',
  translations,
  fillHeight = false,
  className
}: Props<T>) {
  const texts = { ...commonTexts.listCard, ...translations };
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchToggleRef = useRef<HTMLButtonElement>(null);
  const { scrollRef, topSentinelRef, bottomSentinelRef, hiddenAbove, hiddenBelow } = useScrollEdges();

  const isLoaded = !isPending && !error;
  const canSearch = isLoaded && !!getSearchText && items.length > searchThreshold;
  const hasHeaderActions = !!headerNode || canSearch;
  const isSearching = canSearch && isSearchOpen;
  const needle = isSearching ? query.trim().toLowerCase() : '';
  const visibleItems =
    needle && getSearchText ? items.filter((item) => matchesSearch(getSearchText(item), needle)) : items;
  const { getRowKeyboardProps, hintId } = useRowKeyboardNavigation(
    onItemClick ? visibleItems.map((item) => String(getItemKey(item))) : []
  );

  const closeSearch = () => {
    setIsSearchOpen(false);
    setQuery('');
    searchToggleRef.current?.focus();
  };

  return (
    <Card className={cn('gap-0 overflow-hidden p-0', fillHeight && 'flex h-full min-h-0 flex-col', className)}>
      <div
        className={cn(
          '@container relative z-10 border-b transition-shadow duration-200',
          insetVariants({ size }),
          hiddenAbove && 'shadow-[0_6px_10px_-6px_rgb(0_0_0/0.14)]'
        )}
      >
        {/* The search field sits in the header's wrapping row beside the action group, so on a narrow
            card it drops to a full-width line of its own instead of pushing the toggle out of view. */}
        <NoddiCardHeader
          title={title}
          icon={icon}
          iconTile
          isSmall={size === 'small'}
          titleCount={isLoaded && !hideCount ? (titleCount ?? visibleItems.length) : undefined}
          titleCountTotal={needle && !hideCount ? items.length : undefined}
          className={cn(isSearching && '@lg:flex-none')}
          headerNode={
            hasHeaderActions ? (
              <>
                {isSearching && (
                  <NoddiInput
                    // oxlint-disable-next-line jsx-a11y/no-autofocus -- mounts only when the user opens search; focus has to follow the toggle into the field
                    autoFocus
                    aria-label={texts.search(title)}
                    placeholder={texts.searchPlaceholder}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        closeSearch();
                      }
                    }}
                    startAdornment={<NoddiIcon name='Search' size='small' className='text-muted-foreground' />}
                    wrapperClassName='order-last basis-full @lg:order-none @lg:flex-1'
                  />
                )}
                <div className='flex min-w-0 flex-wrap items-center gap-3'>
                  {headerNode}
                  {headerNode && canSearch && (
                    <NoddiSeparator orientation='vertical' className='data-[orientation=vertical]:h-5' />
                  )}
                  {canSearch && (
                    <NoddiIconButton
                      ref={searchToggleRef}
                      iconName={isSearching ? 'X' : 'Search'}
                      aria-label={isSearching ? texts.closeSearch(title) : texts.search(title)}
                      aria-expanded={isSearching}
                      onClick={isSearching ? closeSearch : () => setIsSearchOpen(true)}
                    />
                  )}
                </div>
              </>
            ) : undefined
          }
        />
        {viewAll && (
          <NoddiButton variant='link' size='small' className='mt-1 h-auto px-0' onClick={viewAll.onClick}>
            {viewAll.label}
          </NoddiButton>
        )}
        {toolbar && <div className='pt-4'>{toolbar}</div>}
      </div>
      {/* Hidden, yet still the description of every clickable row, which points at it. */}
      {onItemClick && (
        <span id={hintId} hidden>
          {texts.openHint}
        </span>
      )}
      {/* Contain the scroll only while rows overflow: on a list that fits, it would swallow the page's wheel. */}
      <div
        ref={scrollRef}
        className={cn(
          'overflow-y-auto',
          layout === 'grid' && '@container',
          (hiddenAbove || hiddenBelow) && 'overscroll-contain',
          fillHeight ? 'min-h-0 flex-1' : scrollMode === 'contained' && 'max-h-110'
        )}
      >
        <div ref={topSentinelRef} aria-hidden />
        {isPending ? (
          <ul aria-busy className={listVariants({ layout, gridColumns: layout === 'grid' ? gridColumns : undefined })}>
            {SKELETON_ROWS.map((row) => (
              <li key={row} className={layout === 'rows' ? insetVariants({ size }) : undefined}>
                <NoddiSkeleton className={layout === 'grid' ? 'h-48 w-full' : 'h-10 w-full'} />
              </li>
            ))}
          </ul>
        ) : error ? (
          <div className={insetVariants({ size })}>
            <ApiErrorMessageInternal error={error} onRetry={onRetry} isRetrying={isRetrying} />
          </div>
        ) : visibleItems.length > 0 ? (
          <ul className={listVariants({ layout, gridColumns: layout === 'grid' ? gridColumns : undefined })}>
            {visibleItems.map((item) => {
              const key = getItemKey(item);
              return (
                // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- Enter/Space and focus come from getRowKeyboardProps, as on table rows; a row holding buttons cannot itself be a button
                <li
                  key={key}
                  className={cn(
                    layout === 'rows' ? 'flex items-center gap-4' : 'flex min-w-0 flex-col',
                    layout === 'rows' && insetVariants({ size }),
                    isItemPinned?.(item) && 'bg-warning50',
                    onItemClick &&
                      'cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring'
                  )}
                  onClick={
                    onItemClick &&
                    ((event) =>
                      !shouldSkipRowNavigation({
                        target: event.target,
                        currentTarget: event.currentTarget,
                        selection: window.getSelection()
                      }) && onItemClick(item))
                  }
                  {...getRowKeyboardProps(String(key), () => onItemClick?.(item))}
                >
                  {renderItem(item)}
                </li>
              );
            })}
          </ul>
        ) : (
          <NoddiEmptyContent
            size='small'
            emptyIcon={needle ? 'Search' : 'List'}
            title={needle ? texts.noMatches(query.trim()) : texts.noItems}
          />
        )}
        <div ref={bottomSentinelRef} aria-hidden />
      </div>
      {footer && (
        <div
          className={cn(
            'relative z-10 flex flex-col gap-4 border-t transition-shadow duration-200',
            insetVariants({ size }),
            hiddenBelow && 'shadow-[0_-6px_10px_-6px_rgb(0_0_0/0.14)]'
          )}
        >
          {footer}
        </div>
      )}
    </Card>
  );
}

const matchesSearch = (text: string, needle: string) => {
  const haystack = text.toLowerCase();
  return needle.split(/\s+/).every((token) => haystack.includes(token));
};

/**
 * Which edges of a scroll area hide rows, read off two sentinels at its ends, so the shadows follow
 * scrolling, resizing and rows coming or going without measuring on every scroll event.
 */
const useScrollEdges = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const [hiddenAbove, setHiddenAbove] = useState(false);
  const [hiddenBelow, setHiddenBelow] = useState(false);

  useEffect(() => {
    const root = scrollRef.current;
    const top = topSentinelRef.current;
    const bottom = bottomSentinelRef.current;
    if (!root || !top || !bottom) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const setHidden = entry.target === top ? setHiddenAbove : setHiddenBelow;
          setHidden(!entry.isIntersecting);
        }
      },
      // A couple of pixels of slack so sub-pixel row heights never leave a shadow on at the very end.
      { root, rootMargin: '2px' }
    );
    observer.observe(top);
    observer.observe(bottom);
    return () => observer.disconnect();
  }, []);

  return { scrollRef, topSentinelRef, bottomSentinelRef, hiddenAbove, hiddenBelow };
};
